import classNames from "classnames";
import { Immutable } from "immer";
import React, { useId } from "react";
import { t } from "ttag";

import { getDocument, getWindow } from "@thelabnyc/thelabui/src/utils/dom";

import playButtonCircle from "../../../img/misc/play-button-circle.png";
import { UGCListSubBlock } from "../../models/ugc";
import { focusElement } from "../../utils/keyboardFocus";
import { usePageSetting } from "../../utils/settings";
import { UGCTileWithPost, filterUGCTilesWithPosts } from "../../utils/ugcTiles";
import { Image } from "../Image";
import { SocialIcon } from "../SocialIcon";
import { UGCGallery } from "../UGCGallery";

const MOBILE_WIDTH_THRESHOLD = 769;

const isMobileWidth = (): boolean => {
    const window = getWindow();
    return (window?.innerWidth ?? 0) < MOBILE_WIDTH_THRESHOLD;
};

const removeTileMobileClass = (): void => {
    const document = getDocument();
    const elems = document?.querySelectorAll(".ugc-grid-tile__mobile-clicked");

    if (elems) {
        [].forEach.call(elems, (el: HTMLElement) => {
            el.classList.remove("ugc-grid-tile__mobile-clicked");
        });
    }
};

const buildSocialIcon = (type: string, isHover: boolean): React.JSX.Element => {
    const tileIconClasses = classNames({
        "ugc-grid-tile__icon": true,
        "ugc-grid-tile__icon--hover": isHover,
    });
    return <SocialIcon type={type} className={tileIconClasses} />;
};

const UGCGridTile = (
    props: Immutable<{
        tile: UGCTileWithPost;
        tileKey: number;
        onTileClick: (event: React.MouseEvent<HTMLElement>) => void;
        groupLabel: string;
    }>,
): React.JSX.Element => {
    const { tile } = props;
    const key = props.tileKey;

    const ctaText =
        tile.post.post_type === "video" ? "View video" : "View post";
    const ctaClasses = classNames({
        "ugc-grid-tile__cta": true,
        "al-ugc-grid__view-post": !(tile.post.post_type === "video"),
        "al-ugc-grid__view-video": tile.post.post_type === "video",
    });

    const isTilePhotoOrVideo =
        (tile.post.post_type === "image" || tile.post.post_type === "video") &&
        tile.post.photo;

    const tileBaseClasses = classNames({
        "ugc-grid-tile__base": true,
        "ugc-grid-tile__base--text":
            tile.post.post_type === "text-only" && !tile.post.avatar,
        "ugc-grid-tile__base--avatar":
            tile.post.post_type === "text-only" && tile.post.avatar,
        "ugc-grid-tile__base--photo": tile.post.post_type === "image",
        "ugc-grid-tile__base--video": tile.post.post_type === "video",
    });
    const tileHoverClasses = classNames({
        "ugc-grid-tile__hover": true,
        "ugc-grid-tile__hover--text": !tile.post.avatar,
        "ugc-grid-tile__hover--avatar": tile.post.avatar,
    });

    const playButtonImage = playButtonCircle;

    const tileCTA = tile.post.view_post ? (
        <a title={ctaText} className={ctaClasses} href={`#post-${key}`}>
            {ctaText}
        </a>
    ) : null;

    // Real <img alt> instead of CSS background-image so screen readers get
    // the photo description (A360 #388). Native loading="lazy" replaces the
    // former lazy-background IntersectionObserver path.
    const tileBase = isTilePhotoOrVideo ? (
        <div className={tileBaseClasses}>
            <Image
                src={tile.post.photo}
                alt={tile.post.photo_title || ""}
                className="ugc-grid-tile__photo"
            />
            {tile.post.post_type === "video" && (
                <Image
                    src={playButtonImage}
                    className="ugc-grid-tile__video-icon"
                    alt="play video"
                />
            )}
        </div>
    ) : (
        <div className={tileBaseClasses}>
            {tile.post.avatar && (
                <Image
                    className="ugc-grid-tile__avatar"
                    src={tile.post.avatar}
                    alt={tile.post.avatar_title}
                />
            )}
            <div className="ugc-grid-tile__copy">{tile.post.copy}</div>
            <div className="ugc-grid-tile__attribution">
                <span className="ugc-grid-tile__icon-container">
                    {buildSocialIcon(tile.post.social_media, false)}
                </span>
                <span className="ugc-grid-tile__username">
                    {tile.post.username}
                </span>
            </div>
            {tileCTA}
        </div>
    );

    const tileHover = (
        <button
            className={tileHoverClasses}
            title={`${ctaText} ${tile.post.avatar_title}`}
        >
            {tile.post.avatar && (
                <Image
                    className="ugc-grid-tile__avatar"
                    src={tile.post.avatar}
                    alt={tile.post.avatar_title}
                />
            )}
            <div className="ugc-grid-tile__copy">{tile.post.pull_quote}</div>
            <div className="ugc-grid-tile__attribution">
                <span className="ugc-grid-tile__icon-container">
                    {buildSocialIcon(tile.post.social_media, true)}
                </span>
                <span className="ugc-grid-tile__username">
                    {tile.post.username}
                </span>
            </div>
            {tile.post.view_post && (
                <span className={ctaClasses}>{ctaText}</span>
            )}
        </button>
    );

    return (
        <div
            className="ugc-grid-tile al-ugc-grid-tile"
            onClick={props.onTileClick}
            role="group"
            aria-label={props.groupLabel}
        >
            {tileHover}
            {tileBase}
        </div>
    );
};

export const UGCGrid = (
    props: Immutable<{
        tiles?: UGCListSubBlock[];
        /** Per-brand group label for each tile; falls back to the site's display name. */
        groupLabel?: string;
    }>,
): React.ReactNode => {
    const brandName = usePageSetting("app_display_name") || "";
    const tileGroupLabel =
        props.groupLabel ??
        (brandName ? t`${brandName} on Instagram` : t`Instagram post`);
    const tiles = React.useMemo(
        () => filterUGCTilesWithPosts(props.tiles),
        [props.tiles],
    );
    const [isMobile, setIsMobile] = React.useState(false);
    const [showGallery, setShowGallery] = React.useState(false);
    const [selectedSlide, setSelectedSlide] = React.useState(0);
    const [selectedTileElem, setSelectedTileElem] =
        React.useState<HTMLElement | null>(null);
    const prevTilesLengthRef = React.useRef<number>(0);

    const onGalleryClose = () => {
        setShowGallery(false);

        if (selectedTileElem) {
            const tileButtonElem =
                selectedTileElem.firstElementChild as HTMLElement;
            focusElement(tileButtonElem);

            // Reset the tile to its default state by removing focus state
            selectedTileElem.classList.remove("ugc-grid-tile__mobile-clicked");

            // Also remove focus from any child elements that might be focused
            const focusedChild = selectedTileElem.querySelector(":focus");
            if (focusedChild) {
                (focusedChild as HTMLElement).blur();
            }
        }
    };

    const showGalleryHandler = (
        tileIndex: number,
        targetTileElem: HTMLElement,
    ): void => {
        setShowGallery(true);
        setSelectedSlide(tileIndex);
        setSelectedTileElem(targetTileElem);
    };

    const onTileClick = (
        event: React.MouseEvent<HTMLElement>,
        tileIndex: number,
    ): void => {
        const targetTile = event.currentTarget as HTMLDivElement;
        if (isMobile) {
            const isClicked = targetTile.classList.contains(
                "ugc-grid-tile__mobile-clicked",
            );
            if (!isClicked) {
                removeTileMobileClass();
                targetTile.classList.add("ugc-grid-tile__mobile-clicked");
            } else {
                showGalleryHandler(tileIndex, targetTile);
                targetTile.classList.remove("ugc-grid-tile__mobile-clicked");
            }
        } else {
            showGalleryHandler(tileIndex, targetTile);
        }
    };

    // Required: Event listener needs stable reference for proper cleanup
    const checkMobileState = React.useCallback(() => {
        const mobile = isMobileWidth();

        if (!mobile) {
            removeTileMobileClass();
        }

        setIsMobile(mobile);
    }, []);

    React.useEffect(() => {
        const window = getWindow();
        checkMobileState();
        window?.addEventListener("resize", checkMobileState);

        return () => {
            window?.removeEventListener("resize", checkMobileState);
        };
    }, [checkMobileState]);

    React.useEffect(() => {
        // focus first element in new tiles
        if (tiles.length && prevTilesLengthRef.current > 0) {
            const document = getDocument();
            const elem = document?.querySelectorAll<HTMLElement>(
                `.ugc-grid-tile__hover`,
            )[prevTilesLengthRef.current];
            if (elem) {
                focusElement(elem);
            }
        }

        prevTilesLengthRef.current = tiles.length;
    }, [tiles]);

    const buildTiles = (): React.JSX.Element[] => {
        return tiles.map((tile, cellIndex) => {
            const onTileClickHandler = (
                event: React.MouseEvent<HTMLElement>,
            ) => {
                onTileClick(event, cellIndex);
            };

            return (
                <UGCGridTile
                    key={cellIndex}
                    tile={tile}
                    tileKey={cellIndex}
                    onTileClick={onTileClickHandler}
                    groupLabel={tileGroupLabel}
                />
            );
        });
    };

    const hasTiles = tiles.length > 0;
    const instructionsID = useId();

    return (
        <span>
            {hasTiles ? (
                <p id={instructionsID} className="ada-screenreader-only">
                    {t`This is a carousel of Instagram posts. Use Tab to move between posts, and Enter or Space to open a post.`}
                </p>
            ) : null}
            <div
                role="region"
                aria-label={
                    brandName
                        ? t`${brandName} Instagram gallery carousel`
                        : t`Instagram gallery carousel`
                }
                aria-describedby={hasTiles ? instructionsID : undefined}
                className="ugc-grid__container u-flex-container al-ugc-grid-container"
            >
                {buildTiles()}
            </div>
            {showGallery && tiles.length > 0 && (
                <UGCGallery
                    tiles={tiles}
                    initialSlide={selectedSlide}
                    onCloseGallery={onGalleryClose}
                />
            )}
        </span>
    );
};
